// Peripheral adapters (epic E03): one contract per device family — discovery, self-test, capture,
// normalisation and quality assessment. On the Model H the Android shell exposes the real devices
// through `window.MovoBridge`; in a browser the simulator stands in so the full journey can be shown.

import type { VitalCode } from '@shared';

export type DeviceKind = 'body' | 'bp' | 'oximeter' | 'thermometer';
export type SelfTest = 'ok' | 'missing' | 'fault' | 'calibration_due';

export interface Reading {
  code: VitalCode;
  value: number;
  unit: string;
  /** 0..1 — signal quality as reported by the device / adapter (motion, cuff fit, perfusion index). */
  quality: number;
  device: string;
}

export interface DeviceAdapter {
  kind: DeviceKind;
  name: string;
  selfTest(): Promise<SelfTest>;
  capture(onProgress: (fraction: number) => void, signal: AbortSignal): Promise<Reading[]>;
}

export type Scenario = 'normal' | 'raised_bp' | 'hypertensive_crisis' | 'low_spo2' | 'fever' | 'poor_quality';

export const SCENARIOS: { id: Scenario; label: string }[] = [
  { id: 'normal', label: 'Normal readings' },
  { id: 'raised_bp', label: 'Raised blood pressure (review)' },
  { id: 'hypertensive_crisis', label: 'Hypertensive crisis (urgent)' },
  { id: 'low_spo2', label: 'Low SpO₂ (urgent)' },
  { id: 'fever', label: 'Fever' },
  { id: 'poor_quality', label: 'Poor signal on first attempt' },
];

// --- Android shell bridge ------------------------------------------------------------------

interface BridgeHost {
  request(json: string): void;
  /** Synchronous capability report (MOVO-X Android shell 1.0+). */
  capabilities?(): string;
}

/** What the native shell provides. Older shells without `capabilities()` are treated as full hardware shells. */
export interface ShellCaps {
  shell: string;
  version: string;
  devices: 'hardware' | 'simulated' | 'none';
  printer: 'escpos' | 'preview' | 'none';
  tts: string[];
  stt: boolean;
  lockTask: boolean;
}

let capsCache: ShellCaps | null | undefined;
export function shellCaps(): ShellCaps | null {
  if (capsCache !== undefined) return capsCache;
  const b = typeof window !== 'undefined' ? window.MovoBridge : undefined;
  if (!b) return (capsCache = null);
  try {
    capsCache = b.capabilities ? (JSON.parse(b.capabilities()) as ShellCaps) : { shell: 'android', version: 'unknown', devices: 'hardware', printer: 'escpos', tts: [], stt: false, lockTask: false };
  } catch {
    capsCache = null;
  }
  return capsCache;
}

/** True when the shell's device bridge is driven by its built-in simulator (demonstration builds). */
export const nativeSimulated = () => shellCaps()?.devices === 'simulated';

declare global {
  interface Window {
    MovoBridge?: BridgeHost;
    __movoBridgeResolve?: (id: string, json: string) => void;
  }
}

const pending = new Map<string, (v: unknown) => void>();

export const hasBridge = () => typeof window !== 'undefined' && Boolean(window.MovoBridge);

export function bridgeCall<T>(method: string, params: Record<string, unknown> = {}, timeoutMs = 60000): Promise<T> {
  if (!window.MovoBridge) return Promise.reject(new Error('no native bridge'));
  window.__movoBridgeResolve ??= (id, json) => {
    pending.get(id)?.(JSON.parse(json));
    pending.delete(id);
  };
  const id = crypto.randomUUID();
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => {
      pending.delete(id);
      reject(new Error(`bridge ${method} timed out`));
    }, timeoutMs);
    pending.set(id, (v) => {
      clearTimeout(t);
      const r = v as { ok: boolean; result?: T; error?: string };
      if (r.ok) resolve(r.result as T);
      else reject(new Error(r.error ?? 'bridge error'));
    });
    window.MovoBridge!.request(JSON.stringify({ id, method, params }));
  });
}

class BridgeAdapter implements DeviceAdapter {
  kind: DeviceKind;
  name: string;
  constructor(kind: DeviceKind, name: string) {
    this.kind = kind;
    this.name = name;
  }
  selfTest() {
    return bridgeCall<SelfTest>('device.selfTest', { kind: this.kind }, 10000).catch(() => 'missing' as const);
  }
  capture(onProgress: (f: number) => void, signal: AbortSignal) {
    const timer = setInterval(() => bridgeCall<number>('device.progress', { kind: this.kind }, 2000).then(onProgress).catch(() => {}), 500);
    signal.addEventListener('abort', () => void bridgeCall('device.cancel', { kind: this.kind }).catch(() => {}));
    return bridgeCall<Reading[]>('device.capture', { kind: this.kind }, 120000).finally(() => clearInterval(timer));
  }
}

// --- Simulator -----------------------------------------------------------------------------

const rand = (lo: number, hi: number) => lo + Math.random() * (hi - lo);

export class Simulator {
  scenario: Scenario = 'normal';
  attempts = new Map<DeviceKind, number>();
  faults = new Set<DeviceKind>();

  reset() {
    this.attempts.clear();
  }

  private quality(kind: DeviceKind) {
    const n = (this.attempts.get(kind) ?? 0) + 1;
    this.attempts.set(kind, n);
    if (this.scenario === 'poor_quality' && n === 1) return rand(0.3, 0.55);
    return rand(0.82, 0.98);
  }

  values(kind: DeviceKind): Reading[] {
    const s = this.scenario;
    const q = this.quality(kind);
    const dev = `sim-${kind}`;
    switch (kind) {
      case 'body': {
        const h = Math.round(rand(150, 176));
        const bmi = rand(20, 29);
        return [
          { code: 'height', value: h, unit: 'cm', quality: q, device: dev },
          { code: 'weight', value: Math.round(bmi * (h / 100) ** 2 * 10) / 10, unit: 'kg', quality: q, device: dev },
        ];
      }
      case 'bp': {
        const sys = s === 'hypertensive_crisis' ? rand(182, 196) : s === 'raised_bp' ? rand(146, 162) : rand(108, 132);
        const dia = s === 'hypertensive_crisis' ? rand(112, 124) : s === 'raised_bp' ? rand(90, 99) : rand(68, 84);
        return [
          { code: 'bp_sys', value: Math.round(sys), unit: 'mm[Hg]', quality: q, device: dev },
          { code: 'bp_dia', value: Math.round(dia), unit: 'mm[Hg]', quality: q, device: dev },
        ];
      }
      case 'oximeter':
        return [
          { code: 'spo2', value: Math.round(s === 'low_spo2' ? rand(85, 89) : rand(95, 99)), unit: '%', quality: q, device: dev },
          { code: 'pulse', value: Math.round(s === 'fever' ? rand(98, 112) : s === 'low_spo2' ? rand(100, 118) : rand(64, 88)), unit: '/min', quality: q, device: dev },
        ];
      case 'thermometer':
        return [{ code: 'temp', value: Math.round((s === 'fever' ? rand(38.3, 39.2) : rand(36.4, 37.2)) * 10) / 10, unit: 'Cel', quality: q, device: dev }];
    }
  }
}

const DURATION: Record<DeviceKind, number> = { body: 3500, bp: 9000, oximeter: 6000, thermometer: 3000 };

class SimAdapter implements DeviceAdapter {
  kind: DeviceKind;
  name: string;
  private sim: Simulator;
  constructor(kind: DeviceKind, name: string, sim: Simulator) {
    this.kind = kind;
    this.name = name;
    this.sim = sim;
  }
  async selfTest(): Promise<SelfTest> {
    await new Promise((r) => setTimeout(r, rand(250, 700)));
    return this.sim.faults.has(this.kind) ? 'fault' : 'ok';
  }
  capture(onProgress: (f: number) => void, signal: AbortSignal): Promise<Reading[]> {
    return new Promise((resolve, reject) => {
      const start = performance.now();
      const tick = setInterval(() => {
        const f = Math.min(1, (performance.now() - start) / DURATION[this.kind]);
        onProgress(f);
        if (f >= 1) {
          clearInterval(tick);
          resolve(this.sim.values(this.kind));
        }
      }, 100);
      signal.addEventListener('abort', () => {
        clearInterval(tick);
        reject(new DOMException('aborted', 'AbortError'));
      });
    });
  }
}

export const DEVICE_ORDER: DeviceKind[] = ['body', 'bp', 'oximeter', 'thermometer'];

const NAMES: Record<DeviceKind, string> = {
  body: 'Height & weight station',
  bp: 'Blood pressure monitor',
  oximeter: 'Pulse oximeter',
  thermometer: 'Infrared thermometer',
};

export function createAdapters(sim: Simulator): Record<DeviceKind, DeviceAdapter> {
  const make = (k: DeviceKind) => (hasBridge() && shellCaps()?.devices !== 'none' ? new BridgeAdapter(k, NAMES[k]) : new SimAdapter(k, `${NAMES[k]} (simulator)`, sim));
  return { body: make('body'), bp: make('bp'), oximeter: make('oximeter'), thermometer: make('thermometer') };
}

// --- Printer -------------------------------------------------------------------------------

export type PaperState = 'ok' | 'low' | 'out' | 'unknown';

export async function printerPaper(simulated: PaperState): Promise<PaperState> {
  if (hasBridge() && shellCaps()?.printer !== 'none') return bridgeCall<PaperState>('printer.status', {}, 5000).catch(() => 'unknown' as const);
  return simulated;
}

/** Prints on the built-in 80 mm printer through the native bridge, or via the browser print dialog (the receipt is the only printable element). */
export async function printReceipt(lines: string[]): Promise<void> {
  if (hasBridge() && shellCaps()?.printer !== 'none') {
    await bridgeCall('printer.print', { width: 80, lines }, 30000);
    return;
  }
  window.print();
}
